import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/**
 * Cloud instance summary for the org, aggregated across the org's compute
 * (OpenNebula / s10003) services. Each service's instances are listed through
 * the @mgmt v3 strangler proxy (`/services/:id/s10003/instances`) — the same
 * authorized front door the compute console uses — then bucketed by power state.
 *
 * Pass the compute service IDs (filtered by OPENNEBULA_SCOPE at the call site,
 * mirroring useKubernetesSummary); the summary fans out per service.
 */

export interface InstanceSummary {
  total: number;
  running: number;
  stopped: number;
  error: number;
  other: number;
  /** true if any per-service list failed, so the status split is partial. */
  partial: boolean;
}

interface RawInstance {
  status?: string;
  state?: string;
}

interface InstancesEnvelope {
  status?: number;
  data?: { instances?: RawInstance[] } | RawInstance[];
  instances?: RawInstance[];
  total?: number;
}

function bucketStatus(s: RawInstance): keyof Omit<InstanceSummary, 'total' | 'partial'> {
  // s10003 reports power state in `status`/`state` (e.g. POWERED_ON, ACTIVE,
  // POWERED_OFF, SUSPENDED, ERROR) — normalise case and match either field.
  const v = String(s.status ?? s.state ?? '').toLowerCase();
  if (['running', 'active', 'poweron', 'power_on', 'powered_on'].includes(v)) return 'running';
  if (['stopped', 'off', 'poweroff', 'power_off', 'powered_off', 'suspended', 'shutdown', 'inactive'].includes(v))
    return 'stopped';
  if (['error', 'failed', 'fault'].includes(v)) return 'error';
  return 'other';
}

/** Pull the instance array out of the s10003 list envelope (array | {instances} | {data:…}). */
function readInstances(env: InstancesEnvelope | undefined): RawInstance[] {
  if (!env) return [];
  if (Array.isArray(env.data)) return env.data;
  if (env.data?.instances) return env.data.instances;
  if (env.instances) return env.instances;
  return [];
}

export function useInstanceSummary(serviceIds: string[]) {
  const key = [...serviceIds].sort().join(',');
  return useQuery<InstanceSummary>({
    queryKey: ['v3', 'instances', 'summary', key],
    enabled: serviceIds.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const lists = await Promise.all(
        serviceIds.map((id) =>
          serviceApiV3
            .get<InstancesEnvelope>(`/services/${id}/s10003/instances`)
            .then((r) => ({ ok: true, rows: readInstances(r.data) }))
            .catch(() => ({ ok: false, rows: [] as RawInstance[] })),
        ),
      );

      const sum: InstanceSummary = { total: 0, running: 0, stopped: 0, error: 0, other: 0, partial: false };
      for (const { ok, rows } of lists) {
        if (!ok) { sum.partial = true; continue; }
        for (const r of rows) {
          sum.total += 1;
          sum[bucketStatus(r)] += 1;
        }
      }
      return sum;
    },
  });
}
